<template>
  <!--
    The app's single confirmation dialog for destructive actions, mounted once in App.vue and opened with
    confirm_destructive() from "@/core/helpers/confirm_destructive".
    Not a Bootstrap JS modal: it has to work on top of an open Bootstrap modal too (e.g. deleting an object
    version from the object details modal), so it renders its own backdrop above any other modal.
  -->
  <Teleport to="body">
    <Transition name="destructive-confirm">
      <div v-if="state.open && options" class="destructive-confirm" @focusin.stop @keydown.esc.stop="cancel">
        <div class="modal-backdrop show destructive-confirm__backdrop" @click="cancel"></div>

        <div class="modal d-block destructive-confirm__modal" tabindex="-1" role="dialog" aria-modal="true" :aria-labelledby="title_id" @click.self="cancel">
          <div class="modal-dialog modal-dialog-centered destructive-confirm__dialog">
            <form class="modal-content" @submit.prevent="confirm">
              <div class="modal-header border-0 pb-0 align-items-start">
                <div class="d-flex align-items-center gap-4">
                  <div class="symbol symbol-45px flex-shrink-0">
                    <span class="symbol-label bg-light-danger">
                      <KTIcon icon-name="trash" icon-class="fs-2x text-danger" />
                    </span>
                  </div>
                  <h3 :id="title_id" class="modal-title">{{ options.title }}</h3>
                </div>
                <button type="button" class="btn btn-icon btn-sm btn-active-light ms-2" title="Close" aria-label="Close" v-tooltip @click="cancel">
                  <KTIcon icon-name="cross" icon-type="solid" icon-class="fs-1" />
                </button>
              </div>

              <div class="modal-body pt-6">
                <p class="fs-6 text-gray-700 mb-0">{{ options.message }}</p>

                <!-- The affected item: a filled (non-interactive) pill, with a copy button for pasting it below -->
                <!--
                <div v-if="options.subject" class="destructive-confirm__subject d-flex align-items-center gap-2 mt-4 rounded bg-light px-4 py-3">
                  <span class="font-monospace fw-semibold text-dark text-break flex-grow-1">{{ options.subject }}</span>
                  <button
                    type="button"
                    class="btn btn-icon btn-sm btn-active-light-primary flex-shrink-0"
                    :title="copied ? 'Copied' : 'Copy'"
                    :aria-label="copied ? 'Copied' : 'Copy'"
                    @click="copy_subject"
                  >
                    <KTIcon :icon-name="copied ? 'check' : 'copy'" icon-class="fs-3" :class="copied ? 'text-success' : ''" />
                  </button>
                </div>
                -->

                <ul v-if="options.consequences?.length" class="destructive-confirm__consequences mt-5 mb-0 ps-0 list-unstyled">
                  <li v-for="(line, i) in options.consequences" :key="i" class="d-flex gap-2 fs-6 text-gray-700 mb-2">
                    <KTIcon icon-name="information-5" icon-class="fs-4 text-danger" class="flex-shrink-0 mt-1" />
                    <span>{{ line }}</span>
                  </li>
                </ul>

                <div v-if="options.type_to_confirm" class="mt-6">
                  <label :for="input_id" class="form-label fs-6 text-gray-700">
                    Type <span class="font-monospace fw-bold text-dark text-break">{{ options.type_to_confirm }}</span> to confirm
                  </label>
                  <input
                    :id="input_id"
                    ref="input_el"
                    v-model="typed"
                    type="text"
                    class="form-control font-monospace"
                    autocomplete="off"
                    autocapitalize="off"
                    spellcheck="false"
                  />
                </div>
              </div>

              <div class="modal-footer border-0 pt-0 gap-3">
                <button ref="cancel_el" type="button" class="btn btn-light" @click="cancel">{{ options.cancel_button || 'Cancel' }}</button>
                <button type="submit" class="btn btn-danger" :disabled="!can_confirm">{{ options.confirm_button }}</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { destructive_dialog as state, close_destructive_dialog } from "@/core/helpers/confirm_destructive";

const title_id = "destructive-confirm-title";
const input_id = "destructive-confirm-input";

const options = computed(() => state.options);

const typed = ref("");
const copied = ref(false);
const input_el = ref<HTMLInputElement | null>(null);
const cancel_el = ref<HTMLButtonElement | null>(null);

// Surrounding whitespace is ignored, so pasted text with a trailing newline or space still matches
const can_confirm = computed(() => !options.value?.type_to_confirm || typed.value.trim() === options.value.type_to_confirm);

watch(() => state.open, async (open) => {
  if (!open) return;
  typed.value = "";
  copied.value = false;
  await nextTick();
  // Start in the text box when there is one; otherwise on Cancel, so a stray Enter does not confirm
  (input_el.value ?? cancel_el.value)?.focus();
});

const confirm = () => {
  if (can_confirm.value) close_destructive_dialog(true);
};
const cancel = () => close_destructive_dialog(false);

const copy_subject = async () => {
  if (!options.value?.subject) return;
  try {
    await navigator.clipboard.writeText(options.value.subject);
    copied.value = true;
    setTimeout(() => (copied.value = false), 1500);
  } catch (err) {
    console.error("Copy failed", err);
  }
};
</script>

<style scoped>
/* Above Bootstrap modals (1055) and their backdrops (1050) */
.destructive-confirm__backdrop {
  z-index: 1070;
}

.destructive-confirm__modal {
  z-index: 1075;
}

.destructive-confirm__dialog {
  max-width: 520px;
}

.destructive-confirm-enter-active,
.destructive-confirm-leave-active {
  transition: opacity 0.15s linear;
}

.destructive-confirm-enter-active .destructive-confirm__dialog,
.destructive-confirm-leave-active .destructive-confirm__dialog {
  transition: transform 0.2s ease-out;
}

.destructive-confirm-enter-from,
.destructive-confirm-leave-to {
  opacity: 0;
}

.destructive-confirm-enter-from .destructive-confirm__dialog,
.destructive-confirm-leave-to .destructive-confirm__dialog {
  transform: translateY(-24px);
}
</style>
